<script setup lang="ts">
import type { ApplicantId } from '@/types';
import { ref, watch } from 'vue';

export interface ApplicantPropertyShare {
  id: ApplicantId;
  share: number;
}

export interface LoanApplicantInfo {
  preferredName?: string;
  firstName?: string;
  lastName?: string;
}

const props = defineProps<{
  assetNumber: number;
  modelValue: Record<ApplicantId, number>;
  loanApplicants: Record<ApplicantId, LoanApplicantInfo>;
  assetApplicantIndex?: number; // for applicant-declaration, income-expense-request: index-based IDs
  applicants?: Array<{ id: string }>;
  assetApplicantId?: string; // for broker-application, customer-application, customer-quote, user-quote: string IDs
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', v: Record<ApplicantId, number>): void;
}>();

const applicantsData = ref<ApplicantPropertyShare[]>([]);
const errors = ref<Record<ApplicantId, string | undefined>>({});
const totalError = ref<string | undefined>();

const getApplicantIndex = (id: string) => {
  if (!props.applicants) return 0;
  return props.applicants.findIndex((item) => item.id === id);
};

const generateInputId = (applicantId: string) => {
  if (props.assetApplicantIndex !== undefined) {
    // for applicant-declaration, income-expense-request
    return `applicant-${props.assetApplicantIndex}-asset-${getApplicantIndex(applicantId)}-asset-${
      props.assetNumber
    }-property-ownership`;
  }
  // for broker-application, customer-application, customer-quote, user-quote
  return `${props.assetApplicantId}-asset-${props.assetNumber}-${applicantId}-property-ownership`;
};

const validateShare = (share: number): string | undefined => {
  if (share < 0) {
    return 'Value cannot be negative';
  }
  if (share > 100) {
    return 'Value cannot exceed 100%';
  }
  return undefined;
};

const validateTotal = (): string | undefined => {
  const total = applicantsData.value.reduce((sum, item) => sum + item.share, 0);
  if (total === 0) {
    return 'Total ownership must be greater than 0%';
  }
  if (total > 100) {
    return 'Total ownership cannot exceed 100%';
  }
  return undefined;
};

const validateAndEmit = (index: number) => {
  const applicant = applicantsData.value[index];
  // Validate individual field
  errors.value[applicant.id] = validateShare(applicant.share);
  // Validate total
  totalError.value = validateTotal();
  // Always emit the current values (parent form handles save prevention)
  const newValue = applicantsData.value.reduce((obj, item) => {
    obj[item.id] = item.share;
    return obj;
  }, {} as Record<ApplicantId, number>);
  emit('update:modelValue', newValue);
};

const onlyNumbers = (event: KeyboardEvent) => {
  if (event.key < '0' || event.key > '9') {
    event.preventDefault();
  }
};

const getErrorMessage = (applicantId: ApplicantId): string | undefined => {
  // Only return individual field errors
  return errors.value[applicantId];
};

const applicantName = (id: ApplicantId) => {
  const applicant = props.loanApplicants[id];
  if (!applicant) return '';

  const first = applicant.firstName?.trim();
  const last = applicant.lastName?.trim();

  return [first, last].filter(Boolean).join(' ');
};

watch(
  () => props.modelValue,
  (val) => {
    // Default all values to 0 if not provided
    applicantsData.value = Object.entries(val).map(([id, share]) => ({
      id,
      share: share ?? 0,
    }));
  },
  { immediate: true },
);
</script>

<template>
  <div>
    <div class="mt-4 flex w-full flex-col gap-4">
      <div v-for="(applicant, index) in applicantsData" :key="applicant.id">
        <BxInput
          :id="generateInputId(applicant.id)"
          :data-test-id="generateInputId(applicant.id)"
          :model-value="applicant.share.toString()"
          :label="applicantsData.length > 1 ? applicantName(applicant.id) : ''"
          label-type="floating"
          :error-message="getErrorMessage(applicant.id)"
          maxlength="3"
          @update:model-value="applicant.share = $event === '' ? 0 : Number($event)"
          @keypress="onlyNumbers"
          @blur="validateAndEmit(index)"
        >
          <template #trailing>
            <span class="mx-4 text-gray-400">%</span>
          </template>
        </BxInput>
      </div>
    </div>
    <p v-if="totalError" class="mt-2 text-sm text-rose-500">{{ totalError }}</p>
  </div>
</template>
